type PluralForms = {
    one: string;
    few: string;
    many: string;
};

/**
 * Возвращает правильную форму слова в зависимости от числа (для русского языка)
 * @param count - число
 * @param forms - объект с формами слова
 * @param forms.one - форма для 1 (например, "промпт")
 * @param forms.few - форма для 2-4 (например, "промпта")
 * @param forms.many - форма для 5+ (например, "промптов")
 * @returns правильная форма слова
 *
 * @example
 * plural(1, { one: 'промпт', few: 'промпта', many: 'промптов' }) // "промпт"
 * plural(2, { one: 'промпт', few: 'промпта', many: 'промптов' }) // "промпта"
 * plural(5, { one: 'промпт', few: 'промпта', many: 'промптов' }) // "промптов"
 * plural(21, { one: 'промпт', few: 'промпта', many: 'промптов' }) // "промпт"
 */
export const plural = (count: number, forms: PluralForms): string => {
    const absCount = Math.abs(count);
    const lastDigit = absCount % 10;
    const lastTwoDigits = absCount % 100;

    // Для чисел, оканчивающихся на 11-14, всегда используем форму "many"
    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return forms.many;
    }

    // Для чисел, оканчивающихся на 1, используем форму "one"
    if (lastDigit === 1) {
        return forms.one;
    }

    // Для чисел, оканчивающихся на 2-4, используем форму "few"
    if (lastDigit >= 2 && lastDigit <= 4) {
        return forms.few;
    }

    // Для всех остальных случаев используем форму "many"
    return forms.many;
};

/**
 * Возвращает число с правильной формой слова
 * @param count - число
 * @param forms - объект с формами слова
 * @param forms.one - форма для 1
 * @param forms.few - форма для 2-4
 * @param forms.many - форма для 5+
 * @returns строка вида "5 промптов"
 *
 * @example
 * pluralWithCount(1, { one: 'промпт', few: 'промпта', many: 'промптов' }) // "1 промпт"
 * pluralWithCount(2, { one: 'промпт', few: 'промпта', many: 'промптов' }) // "2 промпта"
 * pluralWithCount(5, { one: 'промпт', few: 'промпта', many: 'промптов' }) // "5 промптов"
 */
export const pluralize = (count: number, forms: PluralForms): string => {
    return `${count} ${plural(count, forms)}`;
};
